/**
 * API Client — Authenticated HTTP client for the connector API
 */

import type {
  SubscribersListResponse,
  SubscriberResponse,
  StatsResponse,
  DeliveriesListResponse,
  ErrorPatternsResponse,
  TestWebhookResponse,
  BulkCleanupResponse,
} from '../types/subscribers';

const AUTH_TOKEN_STORAGE = 'auth_token';
const USER_STORAGE = 'user';

export function getAuthToken(): string {
  return localStorage.getItem(AUTH_TOKEN_STORAGE) || '';
}

export function setAuthToken(token: string) {
  localStorage.setItem(AUTH_TOKEN_STORAGE, token);
}

export function clearAuthToken() {
  localStorage.removeItem(AUTH_TOKEN_STORAGE);
  localStorage.removeItem(USER_STORAGE);
}

export function isAuthenticated(): boolean {
  return !!getAuthToken();
}

export function getUser(): any {
  const userStr = localStorage.getItem(USER_STORAGE);
  return userStr ? JSON.parse(userStr) : null;
}

export function logout() {
  clearAuthToken();
  window.location.href = '/login';
}

// Rate limit error type
export interface RateLimitError extends Error {
  status: 429;
  retryAfter: number; // seconds until retry allowed
  limit: number; // max requests per window
  remaining: number; // requests remaining (0 when rate limited)
  reset: number; // Unix timestamp when limit resets
  window: string; // human-readable window (e.g., "15 minutes")
}

export function isRateLimitError(error: any): error is RateLimitError {
  return error && error.status === 429;
}

async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getAuthToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(path, { ...options, headers });

  if (res.status === 401) {
    clearAuthToken();
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText, detail: res.statusText }));
    
    // Special handling for rate limits (429)
    if (res.status === 429) {
      const resetHeader = res.headers.get('RateLimit-Reset');
      const limitHeader = res.headers.get('RateLimit-Limit');
      const remainingHeader = res.headers.get('RateLimit-Remaining');
      
      const reset = resetHeader ? parseInt(resetHeader, 10) : Math.floor(Date.now() / 1000) + 900; // Default 15 min
      const retryAfter = Math.max(0, reset - Math.floor(Date.now() / 1000));
      
      const rateLimitError: RateLimitError = {
        name: 'RateLimitError',
        message: body.detail || body.error || 'Rate limit exceeded. Please wait before making more requests.',
        status: 429,
        retryAfter,
        limit: limitHeader ? parseInt(limitHeader, 10) : 0,
        remaining: remainingHeader ? parseInt(remainingHeader, 10) : 0,
        reset,
        window: body.window || '15 minutes',
      };
      throw rateLimitError;
    }
    
    // `details` carries the actionable half of the message on several
    // endpoints (e.g. why GitHub refused a dismissal) — don't drop it.
    const message = body.error || body.detail || `HTTP ${res.status}`;
    throw new Error(body.details ? `${message}: ${body.details}` : message);
  }

  if (res.status === 204) return {} as T;
  return res.json();
}

// ─── Dashboard ───────────────────────────────────────────────────────────────

export interface DashboardStats {
  events: { total: number; pending: number; completed: number; failed: number };
  deliveries: { total: number; pending: number; success: number; failed: number; dead: number; avg_latency_ms: number | null };
  subscriber_count: number;
  active_subscribers: number;
  ws_client_count: number;
  uptime_seconds: number;
  repo_breakdown: { repository: string; total: number; completed: number; failed: number; latest: string }[];
}

export interface TimeSeriesData {
  period: string;
  bucket_size: string;
  buckets: { bucket: string; events: number; completed: number; failed: number }[];
}

export const api = {
  dashboard: {
    stats: () => apiFetch<DashboardStats>('/api/dashboard/stats'),
    timeseries: (period = '24h', repository?: string) => {
      const params = new URLSearchParams({ period });
      if (repository) params.set('repository', repository);
      return apiFetch<TimeSeriesData>(`/api/dashboard/stats/timeseries?${params}`);
    },
  },

  // ─── Events ──────────────────────────────────────────────────────────────

  events: {
    list: (opts: { 
      page?: number; 
      limit?: number; 
      event_type?: string; 
      status?: string; 
      repository?: string; 
      repository_exact?: string;
      aggregate_id?: string;
      sender?: string;
      sender_exact?: string;
      sender_type?: string;
      branch?: string;
      branch_exact?: string;
      search?: string;
      signature_valid?: string;
      source_id?: string;
    } = {}) => {
      const params = new URLSearchParams();
      const page = opts.page || 1;
      const limit = opts.limit || 50;
      const offset = (page - 1) * limit;
      if (offset > 0) params.set('offset', String(offset));
      params.set('limit', String(limit));
      if (opts.event_type) params.set('event_type', opts.event_type);
      if (opts.status) params.set('status', opts.status);
      if (opts.repository) params.set('repository', opts.repository);
      if (opts.repository_exact) params.set('repository_exact', opts.repository_exact);
      if (opts.aggregate_id) params.set('aggregate_id', opts.aggregate_id);
      if (opts.sender) params.set('sender', opts.sender);
      if (opts.sender_exact) params.set('sender_exact', opts.sender_exact);
      if (opts.sender_type) params.set('sender_type', opts.sender_type);
      if (opts.branch) params.set('branch', opts.branch);
      if (opts.branch_exact) params.set('branch_exact', opts.branch_exact);
      if (opts.search) params.set('search', opts.search);
      if (opts.signature_valid) params.set('signature_valid', opts.signature_valid);
      if (opts.source_id) params.set('source_id', opts.source_id);
      return apiFetch<{ events: any[]; total: number; limit: number; offset: number }>(`/api/events?${params}`);
    },
    get: (id: string) => apiFetch<WebhookEvent & { deliveries?: Delivery[] }>(`/api/events/${id}`),
    listTypes: (observedOnly = false) =>
      apiFetch<{ event_types: EventTypeCatalogEntry[] }>(
        `/api/events/types${observedOnly ? '?observed=true' : ''}`,
      ),
    getFilterOptions: () => apiFetch<{
      repositories: string[];
      senders: string[];
      /** Bot | Organization | User, from the payload's sender.type (issue #212). */
      sender_types?: string[];
      branches: string[];
      event_types: string[];
      sources: Array<{ id: string; name: string; type: string }>;
    }>('/api/events/filters/options'),
    replay: (id: string, subscriberId?: string) =>
      apiFetch<{ replayed: boolean }>(`/api/v2/subscribers/events/${id}/replay`, {
        method: 'POST',
        body: JSON.stringify(subscriberId ? { subscriber_id: subscriberId } : {}),
      }),
  },

  // ─── Subscribers ─────────────────────────────────────────────────────────

  subscribers: {
    list: (activeOnly = false) =>
      apiFetch<Subscriber[]>(`/api/v2/subscribers${activeOnly ? '?active=true' : ''}`),
    get: (id: string) => apiFetch<SubscriberWithStats>(`/api/v2/subscribers/${id}`),
    create: (data: CreateSubscriberInput) =>
      apiFetch<Subscriber>('/api/v2/subscribers', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: Partial<CreateSubscriberInput> & { active?: boolean }) =>
      apiFetch<Subscriber>(`/api/v2/subscribers/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: string, hard = false) =>
      apiFetch<void>(`/api/v2/subscribers/${id}${hard ? '?hard=true' : ''}`, { method: 'DELETE' }),
    test: (id: string) =>
      apiFetch<TestResult>(`/api/v2/subscribers/${id}/test`, { method: 'POST' }),
    filtersHelp: () => apiFetch<any>('/api/v2/subscribers/filters/help'),
    listRepositories: (id: string, opts: { page?: number; limit?: number } = {}) => {
      const params = new URLSearchParams();
      if (opts.page) params.set('page', String(opts.page));
      if (opts.limit) params.set('limit', String(opts.limit));
      const qs = params.toString();
      return apiFetch<SubscriberRepositoryListResponse>(
        `/api/v2/subscribers/${id}/repositories${qs ? `?${qs}` : ''}`,
      );
    },
    addRepositories: (id: string, repositories: Array<{ owner: string; name: string }>) =>
      apiFetch<SubscriberRepositoryAddResponse>(`/api/v2/subscribers/${id}/repositories`, {
        method: 'POST',
        body: JSON.stringify({ repositories }),
      }),
    removeRepository: (id: string, repositoryId: string) =>
      apiFetch<void>(`/api/v2/subscribers/${id}/repositories/${repositoryId}`, { method: 'DELETE' }),
  },

  // ─── Agent Subscriptions ─────────────────────────────────────────────────

  subscriptions: {
    list: (opts: { page?: number; limit?: number; offset?: number; agent_id?: string } = {}) => {
      const params = new URLSearchParams();
      const page = opts.page || 1;
      const limit = opts.limit || 50;
      const offset = opts.offset !== undefined ? opts.offset : (page - 1) * limit;
      if (offset > 0) params.set('offset', String(offset));
      params.set('limit', String(limit));
      if (opts.agent_id) params.set('agent_id', opts.agent_id);
      return apiFetch<{ subscriptions: AgentSubscription[]; total: number; limit: number; offset: number }>(`/api/v1/subscriptions?${params}`);
    },
    get: (id: string) => apiFetch<AgentSubscription>(`/api/v1/subscriptions/${id}`),
    stats: (id: string) => apiFetch<SubscriptionStats>(`/api/v1/subscriptions/${id}/stats`),
    create: (data: CreateSubscriptionInput) =>
      apiFetch<AgentSubscription>('/api/v1/subscriptions', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: Partial<CreateSubscriptionInput>) =>
      apiFetch<AgentSubscription>(`/api/v1/subscriptions/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: string) =>
      apiFetch<void>(`/api/v1/subscriptions/${id}`, { method: 'DELETE' }),
    deliveries: (id: string, opts: { limit?: number; status?: string } = {}) => {
      const params = new URLSearchParams();
      if (opts.limit) params.set('limit', String(opts.limit));
      if (opts.status) params.set('status', opts.status);
      return apiFetch<{ deliveries: SubscriptionDelivery[]; count: number }>(`/api/v1/subscriptions/${id}/deliveries?${params}`);
    },
  },

  // ─── HTTP Subscribers ────────────────────────────────────────────────────

  httpSubscribers: {
    list: (opts: { active?: boolean; name?: string; search?: string; page?: number; limit?: number } = {}) => {
      const params = new URLSearchParams();
      if (opts.active !== undefined) params.set('active', String(opts.active));
      if (opts.name) params.set('name', opts.name);
      if (opts.search) params.set('search', opts.search);
      if (opts.page) params.set('page', String(opts.page));
      if (opts.limit) params.set('limit', String(opts.limit));
      return apiFetch<SubscribersListResponse>(`/api/v1/http-subscribers?${params}`);
    },
    get: (id: string) => apiFetch<SubscriberResponse>(`/api/v1/http-subscribers/${id}`),
    create: (data: { name: string; url: string; secret?: string; events?: string[]; active?: boolean }) =>
      apiFetch<SubscriberResponse>('/api/v1/http-subscribers', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: Record<string, unknown>) =>
      apiFetch<SubscriberResponse>(`/api/v1/http-subscribers/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: (id: string) =>
      apiFetch<{ success: boolean; message: string }>(`/api/v1/http-subscribers/${id}`, { method: 'DELETE' }),
    enable: (id: string) =>
      apiFetch<SubscriberResponse>(`/api/v1/http-subscribers/${id}/enable`, { method: 'POST' }),
    disable: (id: string) =>
      apiFetch<SubscriberResponse>(`/api/v1/http-subscribers/${id}/disable`, { method: 'POST' }),
    test: (id: string) =>
      apiFetch<TestWebhookResponse>(`/api/v1/http-subscribers/${id}/test`, { method: 'POST' }),
    stats: (id: string, days?: number) => {
      const params = days ? `?days=${days}` : '';
      return apiFetch<StatsResponse>(`/api/v1/http-subscribers/${id}/stats${params}`);
    },
    deliveries: (id: string, opts: { status?: string; event_id?: string; page?: number; limit?: number } = {}) => {
      const params = new URLSearchParams();
      if (opts.status) params.set('status', opts.status);
      if (opts.event_id) params.set('event_id', opts.event_id);
      if (opts.page) params.set('page', String(opts.page));
      if (opts.limit) params.set('limit', String(opts.limit));
      return apiFetch<DeliveriesListResponse>(`/api/v1/http-subscribers/${id}/deliveries?${params}`);
    },
    retryDelivery: (id: string, deliveryId: string) =>
      apiFetch<{ success: boolean; message: string }>(`/api/v1/http-subscribers/${id}/deliveries/${deliveryId}/retry`, { method: 'POST' }),
    cleanupPending: (id: string, olderThanHours?: number) =>
      apiFetch<BulkCleanupResponse>(`/api/v1/http-subscribers/${id}/deliveries/cleanup`, {
        method: 'POST',
        body: JSON.stringify({ older_than_hours: olderThanHours || 24 }),
      }),
    errors: (id: string, limit?: number) => {
      const params = limit ? `?limit=${limit}` : '';
      return apiFetch<ErrorPatternsResponse>(`/api/v1/http-subscribers/${id}/errors${params}`);
    },
  },

  // ─── Deliveries ──────────────────────────────────────────────────────────

  deliveries: {
    forEvent: (eventId: string) =>
      apiFetch<Delivery[]>(`/api/v2/subscribers/events/${eventId}/deliveries`),
  },

  // ─── Health ──────────────────────────────────────────────────────────────

  health: () => apiFetch<HealthCheck>('/health'),

  // ─── Generic Methods ─────────────────────────────────────────────────────
  
  get: <T = any>(path: string) => apiFetch<T>(path),
  post: <T = any>(path: string, data?: any) =>
    apiFetch<T>(path, { method: 'POST', body: data ? JSON.stringify(data) : undefined }),
  put: <T = any>(path: string, data: any) =>
    apiFetch<T>(path, { method: 'PUT', body: JSON.stringify(data) }),
  patch: <T = any>(path: string, data: any) =>
    apiFetch<T>(path, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: <T = any>(path: string) =>
    apiFetch<T>(path, { method: 'DELETE' }),
};

// ─── Types ─────────────────────────────────────────────────────────────────

export interface WebhookEvent {
  id: string;
  github_delivery_id: string;
  event_type: string;
  action?: string | null;
  repository: string;
  sender: string;
  /** GitHub's sender.type — Bot, Organization or User (issue #212). */
  sender_type?: string | null;
  branch?: string | null;
  installation_id?: number | null;
  payload: any;
  github_timestamp?: string | null;
  processing_status: 'pending' | 'completed' | 'failed' | 'processing';
  aggregate_id?: string | null;
  signature_valid: boolean;
  received_at: string;
  created_at: string;
  source_id?: string | null;
  source_name?: string | null;
  source_type?: string | null;
  event_catalog?: EventTypeCatalogEntry | null;
}

export interface EventPresentationField {
  label: string;
  path: string;
  format?: 'text' | 'status' | 'date' | 'url' | 'sha' | 'count' | 'boolean' | 'list';
}

export interface EventTypeCatalogEntry {
  event_type: string;
  display_name: string;
  category: string;
  description?: string | null;
  presentation: {
    entity_key?: string;
    title_path?: string;
    fields?: EventPresentationField[];
  };
  known_actions: string[];
  first_seen_at?: string | null;
  last_seen_at?: string | null;
  event_count: number;
}

export interface Delivery {
  id: string;
  subscriber_id: string;
  event_id: string;
  status: 'pending' | 'success' | 'failed' | 'dead';
  attempt: number;
  status_code: number | null;
  latency_ms: number | null;
  error: string | null;
  response_body: string | null;
  created_at: string;
  delivered_at: string | null;
  subscriber_name?: string;
  event_type?: string;
  event_action?: string | null;
  repository?: string;
  delivery_id?: string;
  max_attempts?: number;
}

export interface Subscriber {
  id: string;
  name: string;
  url: string | null;
  secret: string | null;
  events: string[];
  filters: any;
  active: boolean;
  delivery_config: { timeout_ms: number; max_retries: number; backoff_base_ms: number };
  batching_config?: { enabled: boolean; window_ms: number; max_batch_size: number; group_by: string[] } | null;
  filter_version?: number;
  created_at: string;
  updated_at: string;
}

export interface SubscriberWithStats extends Subscriber {
  delivery_stats: {
    total: number;
    success: number;
    failed: number;
    dead: number;
    avg_latency_ms: number | null;
    last_delivery_at: string | null;
  };
  // Flattened convenience fields (may or may not exist depending on API)
  delivery_count?: number;
  success_rate?: number;
  api_version?: number;
  batching?: { enabled: boolean; window_ms: number; max_batch_size: number; group_by: string[] } | null;
  max_attempts?: number;
}

export interface CreateSubscriberInput {
  name: string;
  url?: string;
  secret?: string;
  events: string[];
  filters?: any;
  delivery_config?: { timeout_ms?: number; max_retries?: number; backoff_base_ms?: number };
  batching?: { enabled: boolean; window_ms?: number; max_batch_size?: number; group_by?: string[] };
}

export interface SubscriberRepository {
  repository_id: string;
  owner: string;
  name: string;
  full_name: string;
  created_at: string;
}

export interface SubscriberRepositoryListResponse {
  data: SubscriberRepository[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface SubscriberRepositoryAddResponse {
  added: number;
  repositories: SubscriberRepository[];
  missing: Array<{ owner: string; name: string }>;
}

export interface TestResult {
  success: boolean;
  status_code: number | null;
  latency_ms: number | null;
  error?: string | null;
  message?: string;
}

export interface HealthCheck {
  status: 'healthy' | 'degraded' | 'unhealthy';
  timestamp: string;
  version: string;
  uptime_seconds: number;
  checks: Record<string, { status: 'up' | 'down'; latency_ms?: number; error?: string }>;
}

// ─── Agent Subscriptions Types ──────────────────────────────────────────────

export interface AgentSubscription {
  id: string;
  agent_id: string;
  agent_name: string;
  repositories: string[];
  event_types: string[];
  actions: string[] | null;
  delivery_mode: 'realtime' | 'aggregate' | 'on_demand';
  delivery_config: {
    webhook_url?: string;
    webhook_secret?: string;
    trigger_events?: string[];
    window_minutes?: number;
    event_threshold?: number;
    timeout_ms?: number;
  };
  webhook_url: string | null;
  webhook_secret: string | null;
  /** Reads no longer echo the secret; this says whether one is set. */
  has_webhook_secret?: boolean;
  websocket_enabled: boolean;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateSubscriptionInput {
  agent_name: string;
  repositories: string[];
  event_types: string[];
  actions?: string[];
  delivery_mode: 'realtime' | 'aggregate' | 'on_demand';
  webhook_url?: string;
  webhook_secret?: string;
  websocket_enabled?: boolean;
  active?: boolean;
  delivery_config?: {
    trigger_events?: string[];
    window_minutes?: number;
    event_threshold?: number;
    timeout_ms?: number;
  };
}

export interface SubscriptionStats {
  total_deliveries: number;
  successful_deliveries: number;
  failed_deliveries: number;
  pending_deliveries: number;
  success_rate: number;
  avg_latency_ms: number | null;
  last_delivery_at: string | null;
  aggregate_count: number;
}

export interface SubscriptionDelivery {
  id: string;
  subscription_id: string;
  aggregate_id: string;
  status: 'pending' | 'delivered' | 'failed';
  retry_count: number;
  delivered_at: string | null;
  error_message: string | null;
  created_at: string;
  // Joined from event_aggregates
  entity_type?: string;
  entity_id?: string;
  repository?: string;
}
